import React, { useRef, useState } from 'react';
import { Upload, Image as ImageIcon, X } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';
const absUrl = (u) => (!u ? '' : /^https?:\/\//.test(u) ? u : `${API}${u}`);

/**
 * AdminImageUploadInput — used inside AdminRecordForm for any field whose key
 * matches the IMAGE_FIELDS allow-list (currently just `image`). Mirrors the
 * SiteSettingsPanel uploader but inline-compact so it sits naturally inside a
 * 2-column grid. POSTs to `/api/admin/upload/image` with the supplied prefix
 * and writes the returned URL back to the form via `onChange(url)`.
 *
 * The URL input remains visible — admins can still paste a CDN link without
 * touching the upload button.
 */
const AdminImageUploadInput = ({ value, onChange, prefix = 'category', testId = 'admin-image-upload' }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await adminApi.uploadImage(f, prefix);
      onChange(r.url);
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-start gap-2" data-testid={`${testId}-wrap`}>
      {value ? (
        <div className="relative shrink-0">
          <img
            src={absUrl(value)}
            alt="preview"
            className="h-12 w-12 object-cover rounded border border-gray-200"
            data-testid={`${testId}-preview`}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full p-0.5 hover:bg-red-50 hover:border-red-300"
            data-testid={`${testId}-clear`}
            aria-label="Remove image"
          >
            <X className="w-3 h-3 text-gray-600" />
          </button>
        </div>
      ) : (
        <div className="h-12 w-12 shrink-0 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400" data-testid={`${testId}-empty`}>
          <ImageIcon className="w-5 h-5" />
        </div>
      )}
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-input`} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-trigger`}
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {uploading ? 'Uploading…' : (value ? 'Replace' : 'Upload')}
          </Button>
        </div>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an image URL"
          className="text-xs h-8"
          data-testid={`${testId}-url`}
        />
      </div>
    </div>
  );
};

export default AdminImageUploadInput;
